import { useEffect, useRef, useState } from "react";
import {
  Box, HStack, Select, Slider, SliderFilledTrack, SliderThumb, SliderTrack, Text,
} from "@chakra-ui/react";
import { observationDay } from "../../utils/satelliteProducts";

type Entry = {
  satellite_data_id: number;
  acquisition_date?: string | null;
  ingestion_date?: string | null;
  created_at: string;
};

type Props = {
  /** Available entries, newest first. */
  entries: Entry[];
  selectedId: number | null | undefined;
  onChange: (satelliteDataId: number) => void;
  label: React.ReactNode;
};

export default function AcquisitionDatePicker({ entries, selectedId, onChange, label }: Props) {
  // The slider runs oldest (left) to newest (right).
  const chronological = [...entries].reverse();
  const selectedIndex = Math.max(0, chronological.findIndex((e) => e.satellite_data_id === selectedId));
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const isPointerDown = useRef(false);
  const shownIndex = dragIndex ?? selectedIndex;

  useEffect(() => setDragIndex(null), [selectedId, entries.length]);

  // Pointer drags select on release, so dragging doesn't load every date passed over.
  // Keyboard steps select immediately because Chakra doesn't fire onChangeEnd for them.
  const commit = (index: number) => {
    isPointerDown.current = false;
    setDragIndex(null);
    const entry = chronological[index];
    if (entry && entry.satellite_data_id !== selectedId) onChange(entry.satellite_data_id);
  };

  return (
    <Box>
      <Text fontWeight="bold" mb={1}>{label}</Text>
      <Select
        isDisabled={!entries.length}
        value={selectedId != null ? String(selectedId) : ""}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        {entries.map((e) => (
          <option key={e.satellite_data_id} value={String(e.satellite_data_id)}>
            {observationDay(e)}
          </option>
        ))}
      </Select>
      {entries.length > 1 && (
        <Box px={2} pt={2}>
          <Slider
            aria-label="Acquisition date"
            min={0}
            max={chronological.length - 1}
            step={1}
            value={shownIndex}
            // Changes from elsewhere (e.g. the map card) must not focus the thumb and scroll the page to it.
            focusThumbOnChange={false}
            onPointerDownCapture={() => { isPointerDown.current = true; }}
            onKeyDownCapture={() => { isPointerDown.current = false; }}
            onChange={(index) => (isPointerDown.current ? setDragIndex(index) : commit(index))}
            onChangeEnd={commit}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <HStack justify="space-between" fontSize="xs" color="gray.500">
            <Text>{observationDay(chronological[0])}</Text>
            {dragIndex != null && (
              <Text fontWeight="bold">{observationDay(chronological[dragIndex])}</Text>
            )}
            <Text>{observationDay(chronological[chronological.length - 1])}</Text>
          </HStack>
        </Box>
      )}
    </Box>
  );
}
